import { useEffect, useRef } from "react";
import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../app/hooks";
import { isContrastMode } from "../../../app/contrastSlice";
import { miwChartColors, utahChartColors } from "../../reports/shared/reportUtil";

interface GraphicProps {
	totalScore: number;
	maxScore: number;
}

export default function TotalScoreGraphic(props: GraphicProps) {
	const echartsRef = useRef<any>(null);
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	let { backgroundColor, foregroundColor, chartPalette } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	} else {
		foregroundColor = "#999";
	}

	const fontFamily = "Montserrat";

	const option: EChartsOption = {
		series: [
			{
				type: "gauge",
				startAngle: 180,
				endAngle: 60,
				min: 0,
				max: props.maxScore,
				splitNumber: props.maxScore == 30 ? 6 : 3,
				itemStyle: {
					color:
						foregroundColor == "#ffff00"
							? foregroundColor
							: chartPalette
								? chartColors[0]
								: chartColors[1],
					shadowColor: foregroundColor,
					shadowBlur: 10,
					shadowOffsetX: 2,
					shadowOffsetY: 2,
				},
				progress: {
					show: true,
					roundCap: true,
					width: 15,
				},
				pointer: {
					show: false,
				},
				axisLine: {
					roundCap: true,
					lineStyle: {
						width: 15,
					},
				},
				axisTick: {
					distance: 2,
					splitNumber: 5,
					lineStyle: {
						width: 1,
						color: foregroundColor,
					},
				},
				splitLine: {
					distance: 2,
					length: 10,
					lineStyle: {
						width: 2,
						color: foregroundColor,
					},
				},
				axisLabel: {
					distance: 25,
					color: foregroundColor,
					fontSize: 10,
				},
				detail: {
					width: "20%",
					offsetCenter: ["15%", "10%"],
					height: 30,
					valueAnimation: true,
					formatter: function (value: number) {
						return "{value|" + value + "}{max|/ " + props.maxScore + "}";
					},
					rich: {
						value: {
							fontSize: 40,
							fontFamily: fontFamily,
							fontWeight: "bold",
							color:
								foregroundColor == "#ffff00"
									? foregroundColor
									: chartPalette
										? chartColors[0]
										: chartColors[1],
						},
						max: {
							fontSize: 18,
							fontFamily: fontFamily,
							color: foregroundColor,
							padding: [0, 0, -20, 10],
						},
					},
				},
				data: [
					{
						value: props.totalScore,
					},
				],
			},
		],
	};

	useEffect(() => {
		const chart = echartsRef.current?.getEchartsInstance();
		if (!chart) return;
		// if not in contrast mode or we are using white on black, dont change chart colors on print.
		if (!isContrastMode() || backgroundColor === "#000000") return;

		const handleBeforePrint: EChartsOption = () => {
			chart.setOption({
				series: [
					{
						itemStyle: {
							color: "#000",
							shadowColor: "transparent",
						},
						axisTick: {
							lineStyle: {
								color: "#000",
							},
						},
						splitLine: {
							lineStyle: {
								color: "#000",
							},
						},
						axisLabel: {
							color: "#000",
						},
						detail: {
							rich: {
								value: {
									color: "#000",
								},
								max: {
									color: "#000",
								},
							},
						},
					},
				],
			});
		};

		const handleAfterPrint = () => {
			chart.setOption({
				series: [
					{
						itemStyle: {
							color:
								foregroundColor == "#ffff00"
									? foregroundColor
									: chartPalette
										? chartColors[0]
										: chartColors[1],
							shadowColor: foregroundColor,
						},
						axisTick: {
							lineStyle: {
								color: foregroundColor,
							},
						},
						splitLine: {
							lineStyle: {
								color: foregroundColor,
							},
						},
						axisLabel: {
							color: foregroundColor,
						},
						detail: {
							rich: {
								value: {
									color:
										foregroundColor == "#ffff00"
											? foregroundColor
											: chartPalette
												? chartColors[0]
												: chartColors[1],
								},
								max: {
									color: foregroundColor,
								},
							},
						},
					},
				],
			});
		};

		window.addEventListener("beforeprint", handleBeforePrint);
		window.addEventListener("afterprint", handleAfterPrint);

		return () => {
			window.removeEventListener("beforeprint", handleBeforePrint);
			window.removeEventListener("afterprint", handleAfterPrint);
		};
	}, [backgroundColor]);

	return <ReactEcharts ref={echartsRef} option={option} className="w-72 !h-80" />;
}
